/**
 * Quantization helpers — pack continuous sim values into small integers for
 * the wire, and unpack them back. The precision is deliberately coarse: at
 * naval speeds the human eye cannot tell, and the client interpolates anyway.
 *
 * Position : u16 per axis over [0, WORLD_SIZE)  -> ~0.125 m precision @ 8192
 * Angle    : u16 over [-PI, PI)                 -> ~0.0001 rad precision
 * Speed    : i16 in cm/s                        -> exact enough, ±327 m/s range
 */
import { WORLD_SIZE, PORT } from './config.js';

const POS_SCALE = 65535 / WORLD_SIZE;
/** Port-local position quantization (u16 over [0, size)) — sub-mm precision on the small plaza plane,
 *  independent of WORLD_SIZE so avatar transforms never share the sea's coarse scale.
 *
 *  ⚠ Onda Parede-invisível-4 (THE capital wall): `size` MUST be the walking room's OWN plaza size. This
 *  helper was born with the 64 m village hard-coded, and the 96 m capital (Onda 6.5) kept riding it — so
 *  every avatar transform east/north of 64 m was CLAMPED on the wire. The client reconciled its walker to
 *  the clamped value each snapshot and rubber-banded on an invisible line bisecting the capital: the wall
 *  "on the way to the casino" that kept coming back after every other fix, and the reason the relocated
 *  casino wing (x 67–91) read as switched off (its Play prompts sat beyond a line no one could cross).
 *  Both sides derive the SAME size from S_PORTWELCOME (kind + outpostId → village/capital), so the wire
 *  format is unchanged (still one u16 per axis, v54). */
export function packPortPos(v: number, size: number = PORT.SIZE): number {
  const c = v < 0 ? 0 : v > size ? size : v;
  return Math.round(c * (65535 / size)) & 0xffff;
}
export function unpackPortPos(q: number, size: number = PORT.SIZE): number {
  return q / (65535 / size);
}
const ANGLE_SCALE = 65535 / (Math.PI * 2);
const TWO_PI = Math.PI * 2;

export function packPos(v: number): number {
  // clamp into world bounds, then scale to u16
  const c = v < 0 ? 0 : v > WORLD_SIZE ? WORLD_SIZE : v;
  return Math.round(c * POS_SCALE) & 0xffff;
}
export function unpackPos(q: number): number {
  return q / POS_SCALE;
}

export function packAngle(rad: number): number {
  // normalize to [0, 2PI) then scale
  let a = rad % TWO_PI;
  if (a < 0) a += TWO_PI;
  return Math.round(a * ANGLE_SCALE) & 0xffff;
}
export function unpackAngle(q: number): number {
  // return in [-PI, PI)
  let a = q / ANGLE_SCALE;
  if (a >= Math.PI) a -= TWO_PI;
  return a;
}

/** Speed/velocity component as signed centimeters per second. */
export function packSpeed(mps: number): number {
  const cm = Math.round(mps * 100);
  return cm < -32768 ? -32768 : cm > 32767 ? 32767 : cm;
}
export function unpackSpeed(q: number): number {
  return q / 100;
}

/** A 0..1 unit float into a u8 (sail trim, throttle, etc.). */
export function packUnit(v: number): number {
  const c = v < 0 ? 0 : v > 1 ? 1 : v;
  return Math.round(c * 255) & 0xff;
}
export function unpackUnit(q: number): number {
  return q / 255;
}
